import React, {
    useContext,
    useEffect,
    useReducer,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    Box,
    Grid,
    Checkbox,
    makeStyles,
    Typography,
    FormControlLabel,
    Button,
    Drawer,
    IconButton,
} from '@material-ui/core';
import moment from 'moment';
import { isEmpty, isEqual } from 'lodash';
import KeyStore from 'utils/KeyStore';
import update from 'immutability-helper';
import ModalUtils from 'utils/ModalUtils';
import DateUtils from 'lib/DateUtils';
import { useQuery } from '@apollo/client';
import StringUtils from 'lib/StringUtils';
import { EmployeeType } from 'utils/enum/UserEnum';
import { FetchPolicy, CALENDAR_SOURCE, MEDIA_QUERY } from 'utils/enum/Core';
import Header from 'components/widgets/Header';
import Filter from 'components/widgets/Filter';
import CallIcon from '@material-ui/icons/Call';
import Permissions from 'utils/enum/Permissions';
import If from 'components/widgets/conditional/If';
import ButtonStyles from 'styles/theme/Button';
import Container from 'components/widgets/Container';
import UserContext from 'components/context/UserContext';
import MainPanel from 'components/widgets/calendar/MainPanel';
import DateRangeRoundedIcon from '@material-ui/icons/DateRangeRounded';
import AddOutlinedIcon from '@material-ui/icons/AddOutlined';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CloseIcon from '@material-ui/icons/Close';
import ActivitiesQuery from 'services/graphQL/query/crm/ActivitiesQuery';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import AvatarWithLabel from 'components/widgets/AvatarWithLabel';
import InputSearch from 'components/widgets/InputSearch';
import {
    ActivityType,
    EventStatus,
    EventColors,
    ActivityStatuses,
    TaskStatus,
    TaskStatusInput,
    InCalendarEventStatus,
} from 'utils/enum/ActivitiesEnum';
import UserQuery from 'services/graphQL/query/UserQuery';
import ServiceQuery from 'services/graphQL/query/service/ServiceQuery';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => ({
    header: {
        marginTop: theme.spacing(1),
    },
    root: {
        flexGrow: 1,
        height: '100%',
        overflow: 'hidden',
        background: theme.palette.background.white,
        boxShadow: '1px 2px 5px 0px #C1C1C1',
        display: 'contents',
        alignItems: 'center',
    },
    gridItem: {
        height: '100%',
        overflow: 'hidden',
        padding: '16px',
        backgroundColor: theme.palette.background.white,
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            padding: '8px',
        },
    },
    title: {
        color: theme.palette.text.boulderGray,
        display: 'inline-block',
        marginRight: '12px',
    },
    routeButton: {
        '&:hover': {
            backgroundColor: 'transparent',
        },
        padding: 0,
    },
    filterPanel: {
        marginRight: '10px',
        overflowY: 'auto',
        [`@media (max-width: ${MEDIA_QUERY.W_992}px)`]: {
            display: 'none',
        },
    },
    filterPanelMobile: {
        width: '280px',
        padding: '16px',
        overflowY: 'auto',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            width: '100%',
            maxWidth: '300px',
        },
    },
    drawerHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '& h6': {
            fontWeight: 500,
            fontSize: '16px',
        },
    },
    mainGridItem: {
        [`@media (max-width: ${MEDIA_QUERY.W_992}px)`]: {
            maxWidth: '100%',
            flexBasis: '100%',
        },
    },
    list: {
        marginLeft: theme.spacing(1),
    },
    listItemIcon: {
        minWidth: theme.spacing(4),
    },
    listItemText: {
        '& span': {
            fontWeight: 500,
            color: theme.palette.text.boulder,
            fontSize: '13px',
        },
    },
    label: {
        marginRight: theme.spacing(1),
        color: theme.palette.text.gray,
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            fontSize: '13px',
        },
    },
    lotContainer: {
        marginRight: theme.spacing(1.5),
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            marginBottom: theme.spacing(1),
            width: '100%',
            '& > div:first-of-type': {
                flex: 1,
            },
        },
    },
    filter: {
        width: 120,
    },
    defaultActivity: {
        border: 'none',
    },
    ScheduledCheckbox: {
        '& .MuiButtonBase-root': {
            color: theme.palette.border.mountainBlue,
        },
    },
    ConfirmedCheckbox: {
        '& .MuiButtonBase-root': {
            color: EventColors.CONFIRMED.color,
        },
    },
    ShowCheckbox: {
        '& .MuiButtonBase-root': {
            color: EventColors.SHOW.color,
        },
    },
    CompletedCheckbox: {
        '& .MuiButtonBase-root': {
            color: EventColors.COMPLETED.color,
        },
    },
    NoShowCheckbox: {
        '& .MuiButtonBase-root': {
            color: EventColors.NOSHOW.color,
        },
    },
    CancelledCheckbox: {
        '& .MuiButtonBase-root': {
            color: EventColors.CANCELLED.color,
        },
    },
    ToDoCheckbox: {
        '& .MuiButtonBase-root': {
            color: EventColors.TODO.backgroundColor,
        },
    },
    PastDueCheckbox: {
        '& .MuiButtonBase-root': {
            color: EventColors.PASTDUE.color,
        },
    },
    MissedCheckbox: {
        '& .MuiButtonBase-root': {
            color: EventColors.MISSED.color,
        },
    },
    OutgoingCheckbox: {
        '& .MuiButtonBase-root': {
            color: EventColors.OUTGOING.color,
        },
    },
    IncomingCheckbox: {
        '& .MuiButtonBase-root': {
            color: EventColors.INCOMING.color,
        },
    },
    checkboxItem: {
        display: 'block',
        padding: theme.spacing(0.2, 0.5),
        borderRadius: '3px',
        height: '19px',
        lineHeight: '16px',
        fontWeight: '500',
    },
    Scheduled: {
        color: EventColors.SCHEDULED.color,
        border: `1px solid ${EventColors.SCHEDULED.border}`,
    },
    Confirmed: {
        color: EventColors.CONFIRMED.color,
    },
    Completed: {
        color: EventColors.COMPLETED.color,
    },
    ToDo: {
        color: EventColors.TODO.color,
    },
    Show: {
        color: EventColors.SHOW.color,
    },
    NoShow: {
        color: EventColors.NOSHOW.color,
    },
    Cancelled: {
        color: EventColors.CANCELLED.color,
    },
    PastDue: {
        color: EventColors.PASTDUE.color,
    },
    Missed: {
        color: EventColors.MISSED.color,
    },
    Outgoing: {
        color: EventColors.OUTGOING.color,
    },
    Incoming: {
        color: EventColors.INCOMING.color,
    },
    employees: {
        marginTop: '20px',
        marginBottom: '20px',
        '& > span': {
            fontWeight: 500,
            fontSize: '14px',
        },
        '& > div:nth-child(2)': {
            marginTop: '15px',
            '& > div': {
                marginBottom: '3px',
                '& h6': {
                    fontSize: '13px',
                },
            },
        },
    },
    iconFilterCls: {
        position: 'relative',
        right: '25px',
        top: '6px',
        zIndex: '-1',
    },
    filterWidth: {
        width: '170px',
        border: `solid 1px ${theme.palette.border.ghost}`,
        padding: '4px 5px',
        borderRadius: '0.2rem',
        paddingRight: '10px',
        '& div': {
            fontSize: '14px',
            fontWeight: 400,
        },
        [`@media (max-width: ${MEDIA_QUERY.W_992}px)`]: {
            width: '140px',
        },
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            width: '120px',
        },
    },
    filterLabel: {
        fontSize: '15px',
        fontWeight: 500,
        marginRight: theme.spacing(1),
        color: theme.palette.text.gray,
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            fontSize: '13px',
        },
    },
    search: {
        marginRight: '15px',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            marginRight: '8px',
            marginBottom: theme.spacing(1),
            width: '100%',
        },
    },
    headerFilters: {
        marginLeft: '30px',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            flexWrap: 'wrap',
            width: '100%',
            marginLeft: 'auto',
        },
    },
}));
const keyStore = new KeyStore();

const calendarTypes = [{
    id: 'event',
    name: ActivityType.EVENT,
    type: 'type',
    label: 'Appointments',
    children: Object.keys(EventStatus).map((key) => ({
        id: `event-${EventStatus[key]}`,
        name: EventStatus[key],
        type: 'status',
        styles: EventColors[key],
    })),
},
{
    id: 'call',
    type: 'type',
    name: ActivityType.CALL,
    label: 'Calls',
    children: Object.keys({
        SCHEDULED: ActivityStatuses.SCHEDULED,
        MISSED: ActivityStatuses.MISSED,
        OUTGOING: ActivityStatuses.OUTGOING,
        INCOMING: ActivityStatuses.INCOMING,
        PAST_DUE: InCalendarEventStatus.PAST_DUE,
        COMPLETED: ActivityStatuses.COMPLETED,
    }).map((key) => ({
        id: `call-${ActivityStatuses[key]}`,
        name: StringUtils.toPascalCase((ActivityStatuses[key] ?? InCalendarEventStatus[key]).toLowerCase()),
        type: 'status',
        styles: EventColors[key],
    })),
},
{
    id: 'task',
    type: 'type',
    name: ActivityType.TASK,
    label: 'Tasks',
    children: Object.keys({ TO_DO: TaskStatus.TO_DO, PAST_DUE: InCalendarEventStatus.PAST_DUE, COMPLETED: TaskStatus.COMPLETED }).map((key) => ({
        id: `task-${TaskStatus[key] ?? InCalendarEventStatus[key]}`,
        name: StringUtils.toPascalCase((TaskStatus[key] ?? InCalendarEventStatus[key]).toLowerCase()),
        type: 'status',
        styles: EventColors[key],
    })),
}];

// Put the records in a hash by type and status to avoid doing a filter in other functions
const getRecordsByType = (records) => {
    const map = {};
    const today = new Date().toISOString();

    calendarTypes.forEach(({ name: type, children }) => {
        map[type] = {
            records: records
                .filter((c) => c.type.toLowerCase() === type.toLowerCase())
                .map((c) => {
                    const clone = { ...c };
                    if (
                        type === ActivityType.TASK && c.task?.status?.toLowerCase() === TaskStatus.TO_DO.toLowerCase()
                        && DateUtils.isBefore(c.realizationDate, today)
                    ) {
                        clone.task = { ...clone.task, status: InCalendarEventStatus.PAST_DUE };
                    }

                    if (
                        type === ActivityType.CALL && c.call?.status?.toLowerCase() === ActivityStatuses.SCHEDULED.toLowerCase()
                        && DateUtils.isBefore(c.realizationDate, today)
                    ) {
                        clone.call = { ...clone.call, status: InCalendarEventStatus.PAST_DUE };
                    }

                    return clone;
                }),
        };

        if (children) {
            children.forEach(({ name: status }) => {
                map[type][status] = map[type].records
                    .filter((c) => (c.status ?? c.event?.status ?? c.task?.status ?? c.call?.status).toLowerCase() === status.toLowerCase());
            });
        }
    });

    return map;
};

const ACTION_TYPE = {
    SET_RECORD: 'setRecord',
    SET_ASSIGNEE: 'setAssignee',
    ON_CHANGE_LOT: 'onChangeLot',
    ON_CHANGE_DATE: 'onChangeDate',
    ON_CHANGE_VALUE: 'onChangeValue',
    ON_TOGGLE_CHECK: 'onToggleCheck',
    SET_TECHNICIANS_ADVISORS: 'setTechniciansAdvisors',
    SET_PARAMS: 'setParams',
    UPDATE_CRM_ACTIVITY: 'updateCRMActivity',
};

const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPE.ON_CHANGE_DATE: {
        const record = action.payload;

        return update(state, {
            startDate: { $set: record.startDate },
            endDate: { $set: record.endDate },
        });
    }
    case ACTION_TYPE.ON_CHANGE_VALUE: {
        keyStore.setCRMFilter({
            [action.field]: action.payload,
        });
        return update(state, {
            [action.field]: { $set: action.payload },
            records: { $set: [] },
            recordsByType: { $set: {} },
        });
    }
    case ACTION_TYPE.SET_ASSIGNEE: {
        const result = action.payload.map((item) => ({ value: item.userId, label: `${item.firstName} ${item.lastName}` }));
        return update(state, {
            assignee: { $set: result },
        });
    }
    case ACTION_TYPE.UPDATE_CRM_ACTIVITY: {
        const { activityId, input } = action.payload;
        const updateRecords = state.records.map((record) => {
            const clone = { ...record };

            if (clone.activityId === activityId) {
                clone.realizationDate = input.realizationDate;
                if (clone.event) clone.event = { ...clone.event, status: input.event.status };
                if (clone.task) clone.task = { ...clone.task, status: input.task.status === TaskStatusInput.TODO ? TaskStatus.TO_DO : input.task.status };
                if (clone.call) clone.call = { ...clone.call, status: input.call.status };
            }

            return clone;
        });

        const recordsByType = getRecordsByType(updateRecords);
        return update(state, {
            records: { $set: updateRecords },
            recordsByType: { $set: recordsByType },
        });
    }
    case ACTION_TYPE.SET_RECORD: {
        const records = action.payload;
        const recordsByType = getRecordsByType(records);

        return update(state, {
            records: { $set: records },
            recordsByType: { $set: recordsByType },
        });
    }
    case ACTION_TYPE.ON_CHANGE_LOT:
        if (isEqual(state.lots, action.value)) {
            return state;
        }

        keyStore.setCRMFilter({
            lots: action.value,
            assigneeUsers: [],
        });

        return update(state, {
            lots: { $set: action.value },
            assigneeUsers: { $set: [] },
            records: { $set: [] },
            recordsByType: { $set: {} },
        });
    case ACTION_TYPE.ON_TOGGLE_CHECK: {
        const { node, parent, checked } = action.payload;
        let items = [...state.unchecked];

        // If the parent node has been checked then we need to remove all the children
        // from the unchecked list
        if (node.children) {
            items = items.filter((c) => node.children.findIndex((child) => child.id === c) === -1);
        }

        // If it's being checked we remove it from the unchecked list
        if (checked) {
            const index = items.indexOf(node.id);
            if (index !== -1) items.splice(index, 1);
        } else {
            items.push(node.id);

            if (node.children) items = items.concat(node.children.map((c) => c.id));
        }

        // If the node has a parent and all of the parent's children are unchecked we need to uncheck the parent also
        if (parent != null) {
            const uncheckedCount = parent.children.filter((c) => items.indexOf(c.id) !== -1).length;

            if (uncheckedCount === parent.children.length) items.push(parent.id);
            else {
                const index = items.indexOf(parent.id);
                if (index !== -1) items.splice(index, 1);
            }
        }

        return update(state, {
            unchecked: { $set: items },
        });
    }
    case ACTION_TYPE.SET_TECHNICIANS_ADVISORS: {
        const employees = action.payload ?? [];
        return update(state, {
            technicians: { $set: employees.filter((i) => i.employeeType === EmployeeType.TECHNICIAN) },
            advisors: { $set: employees.filter((i) => i.employeeType === EmployeeType.SERVICE_ADVISOR) },
        });
    }
    case ACTION_TYPE.SET_PARAMS: {
        return update(state, {
            params: { $set: action.payload },
        });
    }
    default:
        return state;
    }
};

const isIndeterminate = (node, unchecked) => {
    const children = node.children || [];
    const uncheckedCount = children.filter((c) => unchecked.indexOf(c.id) !== -1).length;

    return uncheckedCount > 0 && uncheckedCount !== node.children.length;
};

const initState = {
    startDate: moment().startOf('month'),
    endDate: moment().endOf('month'),
    nodes: calendarTypes,
    unchecked: calendarTypes
        .filter((c) => [ActivityType.CALL, ActivityType.TASK].includes(c.name))
        .reduce((a, b) => {
            const current = [b.name.toLowerCase()];
            if (b.children) current.push(...b.children.map((c) => c.id));

            return [...a, ...current];
        }, []),
    records: [],
    recordsByType: {},
    totalCount: 0,
    assigneeUsers: [],
    assignee: [],
    lots: [],
    technicians: [],
    advisors: [],
    params: {
        search: null,
        lotName: null,
        selectedAdvisors: [],
    },
};
const Calendar = ({ source }) => {
    const classes = { ...useStyles(), ...buttonStyles() };
    const { availableLots, defaultLot } = useContext(UserContext);
    const filter = keyStore.getCRMFilter() || {};
    const mainPanelReference = useRef();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const isService = source === CALENDAR_SOURCE.SERVICE;
    const [state, dispatch] = useReducer(reducer, {
        ...initState,
        ...(isService ? {
            nodes: initState.nodes.filter((n) => n.name === ActivityType.EVENT),
            unchecked: [...initState.unchecked, `event-${EventStatus.NOSHOW}`, `event-${EventStatus.CANCELLED}`],
        } : {}),
        lots: filter.lots || [],
        assigneeUsers: filter.assigneeUsers || [],
    });

    const toggleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };

    const {
        unchecked,
        technicians,
        advisors,
        params,
    } = state;

    const {
        search,
        lotName,
        selectedAdvisors,
    } = params;

    const CRMOpportunityManagement = keyStore.hasPermission(Permissions.CRM_OPPORTUNITY_MANAGEMENT);
    const updateDateRange = (currentDate, selectedCalendarView) => {
        let momentView = selectedCalendarView.toLowerCase();
        if (momentView === 'list_week') {
            momentView = 'week';
        }

        dispatch({
            type: ACTION_TYPE.ON_CHANGE_DATE,
            payload: {
                startDate: moment(currentDate).startOf(momentView),
                endDate: moment(currentDate).endOf(momentView),
            },
        });
    };

    const crmInput = {
        active: true,
        endDate: state.endDate,
        startDate: state.startDate,
        lots: state.lots.map((item) => item.id ?? item.value),
        assignee: state.assigneeUsers.map((item) => item.value),
    };

    const serviceInput = {
        startDate: state.startDate,
        endDate: state.endDate,
        statuses: unchecked?.length > 0
            ? Object.values(EventStatus).filter((e) => !unchecked.some((u) => u === `event-${e}`))
            : [],
        lots: lotName?.length > 0 ? lotName.map((l) => l.label) : [],
        advisors: selectedAdvisors?.length > 0 ? selectedAdvisors.map((s) => s.value) : [],
        search,
    };

    const {
        data: pullServiceAppointments,
        loading: loadingServiceAppointments,
        error: errorLoadingServiceAppointments,
        refetch: refetchServiceAppointments,
    } = useQuery(ServiceQuery.GET_SERVICE_APPOINTMENTS, {
        variables: serviceInput,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        skip: !isService || lotName == null,
        notifyOnNetworkStatusChange: true,
    });

    const {
        data: pullTechniciansAndAdvisors,
        loading: loadingTechniciansAndAdvisors,
        error: errorLoadingTechniciansAndAdvisors,
    } = useQuery(UserQuery.GET_USER_INFO_AND_CHECK_AT_WORK, {
        variables: {
            types: [
                EmployeeType.SERVICE_ADVISOR,
                EmployeeType.TECHNICIAN,
            ],
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        skip: !isService,
    });

    const lots = (state.lots.length > 0) ? state.lots.map((c) => c.value) : keyStore.getUserLots().map((c) => c.lotId);
    const { data: assignee } = useQuery(UserQuery.GET_RECORD_MANAGERS_AND_SALESPERSON_BY_LOTS, {
        variables: { lots },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        skip: isService,
    });

    const { data, loading, error } = useQuery(ActivitiesQuery.GET_CRM_ACTIVITIES, {
        variables: crmInput,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        notifyOnNetworkStatusChange: true,
        skip: isService,
    });

    const onToggleCheck = (node, parent, checked) => {
        dispatch({
            type: ACTION_TYPE.ON_TOGGLE_CHECK,
            payload: {
                node,
                parent,
                checked,
            },
        });
    };

    useEffect(() => {
        if (defaultLot && availableLots?.length > 0) {
            dispatch({
                type: ACTION_TYPE.SET_PARAMS,
                payload: {
                    ...params,
                    lotName: [{
                        label: defaultLot,
                        value: availableLots.find((a) => a.value === defaultLot)?.id,
                    }],
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultLot, availableLots]);

    useEffect(() => {
        if (errorLoadingTechniciansAndAdvisors) {
            ModalUtils.errorMessage(errorLoadingTechniciansAndAdvisors?.graphQLErrors);
            return;
        }

        if (!loadingTechniciansAndAdvisors) {
            const { getUsersAndSalary } = pullTechniciansAndAdvisors ?? {};
            dispatch({
                type: ACTION_TYPE.SET_TECHNICIANS_ADVISORS,
                payload: getUsersAndSalary,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingTechniciansAndAdvisors, errorLoadingTechniciansAndAdvisors]);

    useEffect(() => {
        if (errorLoadingServiceAppointments) {
            ModalUtils.errorMessage(errorLoadingServiceAppointments?.graphQLErrors);
            return;
        }

        const appointments = pullServiceAppointments?.getServiceAppointments;
        if (!loadingServiceAppointments && Array.isArray(appointments)) {
            dispatch({
                type: ACTION_TYPE.SET_RECORD,
                payload: appointments.slice(),
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingServiceAppointments, errorLoadingServiceAppointments]);

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
            return;
        }

        if (!loading && !isEmpty(data?.getCRMActivities)) {
            dispatch({
                type: ACTION_TYPE.SET_RECORD,
                payload: data.getCRMActivities,
            });
        }
    }, [data, loading, error]);

    useEffect(() => {
        if (!isEmpty(assignee?.getRecordManagersAndSalespersonByLots)) {
            dispatch({
                type: ACTION_TYPE.SET_ASSIGNEE,
                payload: assignee.getRecordManagersAndSalespersonByLots,
            });
        }
    }, [assignee]);

    const updateCRMActivity = (activityId, input) => {
        dispatch({
            type: ACTION_TYPE.UPDATE_CRM_ACTIVITY,
            payload: { activityId, input },
        });
    };

    const getMappedRecords = (recordsByType) => {
        const { nodes } = state;
        let records = [];
        let currentSet = {};

        nodes.forEach((node) => {
            currentSet = recordsByType[node.name] || {};

            if (unchecked.indexOf(node.id) === -1 && Array.isArray(currentSet.records)) {
                if (!Array.isArray(node.children)) records = records.concat(currentSet.records);
                else {
                    node.children.forEach((child) => {
                        if (unchecked.indexOf(child.id) === -1 && Array.isArray(currentSet[child.name])) {
                            records = records.concat(currentSet[child.name]);
                        }
                    });
                }
            }
        });

        return records.map((item) => {
            const { opportunity, customer } = item;
            const customerName = isService
                ? `${customer.firstName} ${customer.lastName}`
                : `${opportunity.prospect.firstName} ${opportunity.prospect.lastName}`;

            switch (item.type.toUpperCase()) {
            case ActivityType.EVENT: {
                const style = EventColors[(item.status ?? item.event.status).replace(' ', '').toUpperCase()] || {};
                return {
                    id: item.activityId,
                    start: new Date(item.startsAt ?? item.event.start),
                    end: new Date(item.endsAt ?? item.event.end),
                    title: customerName,
                    description: item.reason ?? item.note,
                    display: 'block',
                    record: item,
                    textColor: style.textColor,
                    className: classes[(item.status ?? item.event.status).replace(' ', '')],
                    backgroundColor: style.backgroundColor,
                    icon: <DateRangeRoundedIcon fontSize="small" />,
                };
            }
            case ActivityType.CALL: {
                const style = EventColors[(item.call.status).replace(' ', '').toUpperCase()] || {};
                return {
                    id: item.activityId,
                    start: new Date(item.realizationDate),
                    end: new Date(item.realizationDate),
                    title: customerName,
                    description: item.note,
                    display: 'block',
                    record: item,
                    textColor: style.textColor,
                    backgroundColor: style.backgroundColor,
                    className: classes[(item.call.status).replace(' ', '')],
                    icon: <CallIcon fontSize="small" />,
                };
            }
            case ActivityType.TASK: {
                const style = EventColors[(item.task.status).replace(' ', '').toUpperCase()] || {};
                return {
                    id: item.activityId,
                    start: new Date(item.realizationDate),
                    end: new Date(item.realizationDate),
                    title: customerName,
                    description: item.note,
                    display: 'block',
                    record: item,
                    textColor: style.textColor,
                    backgroundColor: style.backgroundColor,
                    className: classes[(item.task.status).replace(' ', '')],
                    icon: <AssignmentTurnedInIcon fontSize="small" />,
                };
            }
            default:
                throw new Error(`Activity Type ${item.type} not implemented.`);
            }
        });
    };

    const onChangeLot = (value) => {
        dispatch({ type: ACTION_TYPE.ON_CHANGE_LOT, value });
    };

    const onChangeValue = (record, field) => {
        dispatch({
            type: ACTION_TYPE.ON_CHANGE_VALUE,
            payload: record,
            field,
        });
    };

    const getLabel = (value, name) => {
        const displayValue = name === EventStatus.SHOW ? value.replace(EventStatus.SHOW, 'Showed') : value;

        return (
            <span
                className={clsx(classes.checkboxItem, classes[name?.replace(' ', '')])}
            >
                {StringUtils.toPascalCase(displayValue.toLowerCase())}
            </span>
        );
    };

    const getTypeLabel = (node) => {
        const count = state.recordsByType[node.name]?.records?.length || 0;
        const value = `${StringUtils.toPascalCase(node.label || node.name.toLowerCase())} (${count})`;

        return getLabel(value, node.name.toLowerCase());
    };

    const getTypeStatusLabel = (parent, node) => {
        const count = (state.recordsByType[parent.name] || {})[node.name]?.length || 0;
        const value = `${node.name} (${count})`;

        return getLabel(value, node.name);
    };

    const onSearch = (text) => {
        dispatch({
            type: ACTION_TYPE.SET_PARAMS,
            payload: {
                ...params,
                search: text,
            },
        });
    };

    const onChangeParam = (name, value) => {
        dispatch({
            type: ACTION_TYPE.SET_PARAMS,
            payload: {
                ...params,
                [name]: value,
            },
        });
    };

    const onOpenForm = () => {
        if (mainPanelReference.current) mainPanelReference.current.onOpenForm();
    };

    const availableLot = (availableLots || []).map((item) => ({ value: item.id, label: item.label }));
    const filteredTechnicians = (technicians ?? []).filter((t) => t.isAtWorkToday);
    const filteredAdvisors = (advisors ?? []).filter((a) => a.isAtWorkToday);

    const renderFilterPanel = () => (
        <>
            {state.nodes.map((node) => (
                <div key={`${node.name}`}>
                    <FormControlLabel
                        label={getTypeLabel(node)}
                        className={classes[`${node.name.toLowerCase()}Checkbox`]}
                        control={(
                            <Checkbox
                                checked={unchecked.indexOf(node.id) === -1}
                                indeterminate={isIndeterminate(node, unchecked)}
                                onChange={(event, checked) => onToggleCheck(node, null, checked)}
                            />
                        )}
                    />
                    {Array.isArray(node.children) && (
                        <Box style={{
                            display: 'flex', flexDirection: 'column', ml: 3, marginLeft: '24px',
                        }}
                        >
                            {node.children.map((child) => (
                                <FormControlLabel
                                    key={`${node.name}-${child.name}`}
                                    checked={unchecked.indexOf(child.id) === -1}
                                    label={getTypeStatusLabel(node, child)}
                                    className={classes[`${child.name.replace(' ', '')}Checkbox`]}
                                    control={<Checkbox onChange={(event, checked) => onToggleCheck(child, node, checked)} />}
                                />
                            ))}
                        </Box>
                    )}
                </div>
            ))}
            {isService && filteredTechnicians.length > 0 && (
                <div className={classes.employees}>
                    <span>Available Technicians</span>
                    <div>
                        {filteredTechnicians.map(({
                            firstName,
                            lastName,
                            userPicture,
                        }, index) => (
                            <AvatarWithLabel
                                key={index}
                                person={{
                                    firstName,
                                    lastName,
                                    picture: userPicture,
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}
            {isService && filteredAdvisors.length > 0 && (
                <div className={classes.employees}>
                    <span>Available Advisors</span>
                    <div>
                        {filteredAdvisors.map(({
                            firstName,
                            lastName,
                            userPicture,
                        }, index) => (
                            <AvatarWithLabel
                                key={index}
                                person={{
                                    firstName,
                                    lastName,
                                    picture: userPicture,
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}
        </>
    );

    return (
        <>
            <Header className={classes.header}>
                <div className={clsx('d-flex-center', classes.headerFilters)}>
                    {!isService && (
                        <>
                            <div className={clsx('d-flex-align-baseline-space-between', classes.lotContainer)}>
                                <Typography
                                    variant="h5"
                                    className={classes.label}
                                >
                                    Lot:
                                </Typography>
                                <Filter
                                    showTooltip
                                    useInternalSearch
                                    maxWidthLabel={200}
                                    records={availableLot}
                                    selectedValues={state.lots}
                                    applyFilter={(record) => onChangeLot(record)}
                                    onClearFilter={() => onChangeLot([])}
                                />
                            </div>
                            <If condition={CRMOpportunityManagement}>
                                <div className="d-flex-align-baseline-space-between">
                                    <Typography
                                        variant="h5"
                                        className={classes.label}
                                    >
                                        Assignee:
                                    </Typography>
                                    <Filter
                                        showTooltip
                                        useInternalSearch
                                        maxWidthLabel={250}
                                        records={state.assignee}
                                        selectedValues={state.assigneeUsers}
                                        applyFilter={(record) => onChangeValue(record, 'assigneeUsers')}
                                        onClearFilter={() => onChangeValue([], 'assigneeUsers')}
                                    />
                                </div>
                            </If>
                        </>
                    )}
                    {isService && (
                        <>
                            <InputSearch
                                customClasses={classes.search}
                                initialSearch={search || ''}
                                onSearch={onSearch}
                            />
                            <div className="d-flex-align-baseline-space-between">
                                <Typography
                                    variant="h5"
                                    className={classes.filterLabel}
                                >
                                    Lot:
                                </Typography>
                                <Filter
                                    useInternalSearch
                                    emptyRecord="All"
                                    maxWidthLabel={150}
                                    className={classes.filterWidth}
                                    records={availableLot}
                                    selectedValues={lotName || []}
                                    applyFilter={(record) => onChangeParam('lotName', record)}
                                    onClearFilter={() => onChangeParam('lotName', [])}
                                />
                                <ArrowDropDownIcon className={classes.iconFilterCls} />
                            </div>
                            <div className="d-flex-align-baseline-space-between">
                                <Typography
                                    variant="h5"
                                    className={classes.filterLabel}
                                >
                                    Advisor:
                                </Typography>
                                <Filter
                                    showTooltip
                                    useInternalSearch
                                    emptyRecord="All"
                                    maxWidthLabel={150}
                                    className={classes.filterWidth}
                                    records={advisors.map((a) => ({ label: `${a.firstName} ${a.lastName}`, value: a.userId }))}
                                    selectedValues={selectedAdvisors}
                                    loading={loadingTechniciansAndAdvisors}
                                    applyFilter={(record) => onChangeParam('selectedAdvisors', record)}
                                    onClearFilter={() => onChangeParam('selectedAdvisors', [])}
                                />
                                <ArrowDropDownIcon className={classes.iconFilterCls} />
                            </div>
                        </>
                    )}
                </div>
                {isService && (
                    <div>
                        <Button
                            size="small"
                            startIcon={<AddOutlinedIcon />}
                            className={classes.containedSecondaryInfo}
                            onClick={onOpenForm}
                        >
                            Create Appointment
                        </Button>
                    </div>
                )}
            </Header>
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={toggleDrawer}
            >
                <div className={classes.filterPanelMobile}>
                    <div className={classes.drawerHeader}>
                        <Typography variant="h6">Filters</Typography>
                        <IconButton onClick={toggleDrawer} size="small">
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <div style={{ marginTop: '16px' }}>
                        {renderFilterPanel()}
                    </div>
                </div>
            </Drawer>
            <Container className={classes.boxContainer}>
                <Grid container className={classes.root}>
                    <Grid item xs={2} className={clsx(classes.gridItem, classes.filterPanel)}>
                        {renderFilterPanel()}
                    </Grid>
                    <Grid item xs={10} className={clsx(classes.gridItem, classes.mainGridItem)}>
                        <MainPanel
                            ref={mainPanelReference}
                            updateDateRange={updateDateRange}
                            records={getMappedRecords(state.recordsByType)}
                            isService={isService}
                            advisors={advisors}
                            refetchServiceAppointments={refetchServiceAppointments}
                            updateCRMActivity={updateCRMActivity}
                            onToggleDrawer={toggleDrawer}
                        />
                    </Grid>
                </Grid>
            </Container>
        </>
    );
};

Calendar.propTypes = {
    source: PropTypes.string.isRequired,
};

export default Calendar;
